import { Alert, Badge, Button, EmptyState, FieldBlock, Input, LoadingState, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { FormRow, FormScreen, FormSection } from '../../components/FormScreen.tsx'
import { api } from '../../lib/api.ts'
import { SwitchCard, normalizarCodigo } from './catalog-shared.tsx'

/**
 * Cadastro de Tipos de relação (SPEC-20260815-0821), padrao FormScreen.
 * Cada relacao tem rotulo DIRETO e REVERSO (exibidos conforme o lado);
 * `isSymmetric` marca quando os dois coincidem e o reverso deixa de existir.
 * Todas as relacoes sao cadastraveis (2026-08-30) — a API so impede excluir o
 * que esta EM USO. A UNICA excecao e , marcada como
 * ANCORA: o servidor procura por esse CODIGO para achar os criterios de aceite
 * (), entao ele nao muda e a relacao nao se exclui.
 * Os rotulos dela seguem livres.
 */

type RelationList = Awaited<ReturnType<Awaited<ReturnType<(typeof api.catalog)['relation-types']['$get']>>['json']>>
type RelationRow = RelationList['relationTypes'][number]

type FormRelacao = {
  namePt: string
  nameEn: string
  reversePt: string
  reverseEn: string
  code: string
  isSymmetric: boolean
  isActive: boolean
  sortOrder: number
}

const VAZIO: FormRelacao = {
  namePt: '',
  nameEn: '',
  reversePt: '',
  reverseEn: '',
  code: '',
  isSymmetric: false,
  isActive: true,
  sortOrder: 0,
}

export function RelationTypesSection({ criarSinal = 0 }: { criarSinal?: number }) {
  const toast = useToast()
  const qc = useQueryClient()

  const lista = useQuery({
    queryKey: ['relation-types'],
    queryFn: async () => (await api.catalog['relation-types'].$get()).json(),
  })

  const [busca, setBusca] = useState('')
  const [editando, setEditando] = useState<{ id: string | null } | null>(null)
  const [form, setForm] = useState<FormRelacao>(VAZIO)
  const [confirmandoExclusao, setConfirmandoExclusao] = useState<string | null>(null)

  useEffect(() => {
    if (criarSinal > 0) abrir(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criarSinal])

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['relation-types'] })
    void qc.invalidateQueries({ queryKey: ['bootstrap'] })
    void qc.invalidateQueries({ queryKey: ['i18n'] })
  }

  const aoFalhar = (erro: unknown) =>
    toast.push({
      tone: 'danger',
      message: erro instanceof Error && erro.message ? erro.message : 'Não foi possível concluir. Tente novamente.',
    })

  const corpoDe = (f: FormRelacao) => ({
    code: normalizarCodigo(f.code || f.namePt),
    name: { 'pt-BR': f.namePt.trim(), ...(f.nameEn.trim() ? { 'en-US': f.nameEn.trim() } : {}) },
    ...(f.isSymmetric
      ? {}
      : {
          reverse: { 'pt-BR': f.reversePt.trim(), ...(f.reverseEn.trim() ? { 'en-US': f.reverseEn.trim() } : {}) },
        }),
    isSymmetric: f.isSymmetric,
    isActive: f.isActive,
    sortOrder: f.sortOrder,
  })

  const criar = useMutation({
    mutationFn: async (f: FormRelacao) => (await api.catalog['relation-types'].$post({ json: corpoDe(f) })).json(),
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Tipo de relação criado.' })
    },
    onError: aoFalhar,
  })

  const atualizar = useMutation({
    mutationFn: async ({ id, f }: { id: string; f: FormRelacao }) =>
      (await api.catalog['relation-types'][':id'].$patch({ param: { id }, json: corpoDe(f) })).json(),
    onSuccess: () => {
      invalidar()
      setEditando(null)
      toast.push({ tone: 'success', message: 'Tipo de relação atualizado.' })
    },
    onError: aoFalhar,
  })

  const alternarAtivo = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      (await api.catalog['relation-types'][':id'].active.$post({ param: { id }, json: { isActive } })).json(),
    onSuccess: invalidar,
    onError: aoFalhar,
  })

  const excluir = useMutation({
    mutationFn: async (id: string) => (await api.catalog['relation-types'][':id'].$delete({ param: { id } })).json(),
    onSuccess: () => {
      invalidar()
      setConfirmandoExclusao(null)
      toast.push({ tone: 'success', message: 'Tipo de relação excluído.' })
    },
    onError: (erro) => {
      setConfirmandoExclusao(null)
      aoFalhar(erro)
    },
  })

  const tipos = lista.data?.relationTypes ?? []
  const emEdicao = editando?.id ? tipos.find((t) => t.id === editando.id) : undefined

  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    if (termo === '') return tipos
    return tipos.filter((t) =>
      `${t.name['pt-BR'] ?? ''} ${t.reverse['pt-BR'] ?? ''} ${t.code}`.toLowerCase().includes(termo),
    )
  }, [tipos, busca])

  const abrir = (alvo: RelationRow | null) => {
    setForm(
      alvo
        ? {
            namePt: alvo.name['pt-BR'] ?? '',
            nameEn: alvo.name['en-US'] ?? '',
            reversePt: alvo.reverse['pt-BR'] ?? '',
            reverseEn: alvo.reverse['en-US'] ?? '',
            code: alvo.code,
            isSymmetric: alvo.isSymmetric,
            isActive: alvo.isActive,
            sortOrder: alvo.sortOrder,
          }
        : { ...VAZIO, sortOrder: (tipos.at(-1)?.sortOrder ?? 0) + 10 },
    )
    setEditando({ id: alvo?.id ?? null })
  }

  const preenchido =
    form.namePt.trim().length >= 2 && (form.isSymmetric || form.reversePt.trim().length >= 2)
  const alterado =
    editando?.id == null
      ? preenchido
      : emEdicao !== undefined &&
        JSON.stringify(corpoDe(form)) !==
          JSON.stringify(
            corpoDe({
              namePt: emEdicao.name['pt-BR'] ?? '',
              nameEn: emEdicao.name['en-US'] ?? '',
              reversePt: emEdicao.reverse['pt-BR'] ?? '',
              reverseEn: emEdicao.reverse['en-US'] ?? '',
              code: emEdicao.code,
              isSymmetric: emEdicao.isSymmetric,
              isActive: emEdicao.isActive,
              sortOrder: emEdicao.sortOrder,
            }),
          )

  // ------------------------------------------------------- formulário ---

  if (editando !== null) {
    const siglaDerivada = normalizarCodigo(form.code || form.namePt)
    const salvarValido = alterado && preenchido && !criar.isPending && !atualizar.isPending

    return (
      <FormScreen
        title={editando.id ? 'Editar tipo de relação' : 'Novo tipo de relação'}
        subtitle={editando.id ? (emEdicao?.name['pt-BR'] ?? emEdicao?.code) : undefined}
        onClose={() => setEditando(null)}
        dirty={alterado}
        dirtyHint={
          editando.id
            ? 'Tudo salvo'
            : form.isSymmetric
              ? 'Preencha o rótulo para salvar'
              : 'Preencha os rótulos direto e reverso para salvar'
        }
        saveLabel={editando.id ? 'Salvar' : 'Criar tipo de relação'}
        saveDisabled={!salvarValido}
        saving={criar.isPending || atualizar.isPending}
        // Exclusão no rodapé, à esquerda (padrão 2026-08-30).
        onDelete={
          emEdicao
            ? () => {
                const nome = emEdicao.name['pt-BR'] ?? emEdicao.code
                if (!confirm(`Excluir o tipo de relação “${nome}”?`)) return
                excluir.mutate(emEdicao.id, { onSuccess: () => setEditando(null) })
              }
            : undefined
        }
        deleteLabel="Excluir tipo de relação"
        deleteDisabled={Boolean(emEdicao?.isSystem) || (emEdicao?.usageCount ?? 0) > 0}
        deleteHint={
          emEdicao?.isSystem
            ? 'Âncora dos critérios de aceite: não pode ser excluída. Desative-a para tirá-la das opções.'
            : (emEdicao?.usageCount ?? 0) > 0
              ? 'Relação em uso: desative-a para tirá-la das opções'
              : undefined
        }
        onSubmit={() => {
          if (!salvarValido) return
          if (editando.id) atualizar.mutate({ id: editando.id, f: form })
          else criar.mutate(form)
        }}
      >
        <FormSection title="Rótulos">
          <FormRow cols="minmax(220px, 480px)">
            <SwitchCard
              title="Relação simétrica"
              description="O mesmo texto vale para os dois lados (ex.: “Relaciona-se com”)."
              checked={form.isSymmetric}
              onChange={(isSymmetric) => setForm((f) => ({ ...f, isSymmetric }))}
            />
          </FormRow>

          <FormRow cols="1fr 1fr">
            <FieldBlock
              label={form.isSymmetric ? 'Rótulo (os dois lados)' : 'Rótulo direto'}
              required
              hint={form.isSymmetric ? 'Ex.: Relaciona-se com.' : 'Do item de origem para o alvo — ex.: Bloqueia.'}
            >
              {({ id }) => (
                <Input
                  id={id}
                  autoFocus
                  value={form.namePt}
                  placeholder="Ex.: Bloqueia"
                  onChange={(e) => setForm((f) => ({ ...f, namePt: e.target.value }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Label (en-US)" hint="Opcional.">
              {({ id }) => (
                <Input
                  id={id}
                  value={form.nameEn}
                  placeholder="Ex.: Blocks"
                  onChange={(e) => setForm((f) => ({ ...f, nameEn: e.target.value }))}
                />
              )}
            </FieldBlock>
          </FormRow>

          {!form.isSymmetric ? (
            <FormRow cols="1fr 1fr">
              <FieldBlock
                label="Rótulo reverso"
                required
                hint="Exibido no item da outra ponta — ex.: É bloqueado por."
              >
                {({ id }) => (
                  <Input
                    id={id}
                    value={form.reversePt}
                    placeholder="Ex.: É bloqueado por"
                    onChange={(e) => setForm((f) => ({ ...f, reversePt: e.target.value }))}
                  />
                )}
              </FieldBlock>
              <FieldBlock label="Reverse label (en-US)" hint="Opcional.">
                {({ id }) => (
                  <Input
                    id={id}
                    value={form.reverseEn}
                    placeholder="Ex.: Is blocked by"
                    onChange={(e) => setForm((f) => ({ ...f, reverseEn: e.target.value }))}
                  />
                )}
              </FieldBlock>
            </FormRow>
          ) : null}
        </FormSection>

        <FormSection title="Identificação">
          <FormRow cols="1fr 1fr 1fr">
            <FieldBlock
              label="Código"
              hint={
                emEdicao?.isSystem
                  ? 'O sistema procura por este código para achar os critérios de aceite — ele não muda. Os rótulos são livres.'
                  : form.code.trim() === '' && form.namePt.trim() !== ''
                    ? `Derivado do rótulo: ${siglaDerivada}. Digite para personalizar.`
                    : 'Identificador técnico, único no workspace.'
              }
            >
              {({ id }) => (
                <Input
                  id={id}
                  value={form.code}

                  disabled={emEdicao?.isSystem}

                  placeholder={siglaDerivada || 'Ex.: BLOCKS'}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                  onBlur={() => setForm((f) => ({ ...f, code: f.code ? normalizarCodigo(f.code) : f.code }))}
                />
              )}
            </FieldBlock>
            <FieldBlock label="Ordem" hint="Menor aparece primeiro no seletor de relações.">
              {({ id }) => (
                <Input
                  id={id}
                  type="number"
                  min={0}
                  value={String(form.sortOrder)}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: Math.max(0, Number(e.target.value) || 0) }))}
                />
              )}
            </FieldBlock>
            <SwitchCard
              title={form.isActive ? 'Tipo ativo' : 'Tipo inativo'}
              description={form.isActive ? 'Disponível ao ligar Itens de Trabalho.' : 'Some das opções; ligações existentes ficam.'}
              checked={form.isActive}
              onChange={(isActive) => setForm((f) => ({ ...f, isActive }))}
            />
          </FormRow>

          {emEdicao ? (
            <p className="adm-hint">
              <i className="fas fa-link" aria-hidden />{' '}
              {emEdicao.usageCount > 0
                ? `Em uso por ${emEdicao.usageCount} ${emEdicao.usageCount === 1 ? 'ligação' : 'ligações'}.`
                : 'Ainda sem uso.'}
              {emEdicao.code === 'ACCEPTANCE_CRITERION_OF'
                ? ' Âncora estrutural: liga critérios de aceite ao item que qualificam.'
                : ''}
            </p>
          ) : null}
        </FormSection>
      </FormScreen>
    )
  }

  // ------------------------------------------------------------ lista ---

  const cabecalho = (
    <div className="adm-head">
      <div>
        <h1 className="adm-head__title">Tipos de relação</h1>
        <p className="adm-head__desc">Rótulo direto e reverso, exibidos conforme o lado da relação entre Itens de Trabalho.</p>
      </div>
      <Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>
        Incluir
      </Button>
    </div>
  )

  if (lista.isPending) return (<>{cabecalho}<LoadingState variant="list" label="Carregando os tipos de relação" /></>)
  if (lista.isError) {
    return (
      <>
        {cabecalho}
        <div className="adm-placeholder">
          <Alert tone="danger" title="Não foi possível carregar os tipos de relação"
            action={<Button variant="secondary" icon="fas fa-rotate-right" onClick={() => void lista.refetch()}>Tentar de novo</Button>}>
            Verifique a conexão e tente novamente.
          </Alert>
        </div>
      </>
    )
  }

  return (
    <>
      {cabecalho}

      <div className="adm-toolbar">
        <div className="adm-toolbar__search">
          <i className="fas fa-search" aria-hidden />
          <input type="search" placeholder="Filtrar por rótulo ou código…" aria-label="Filtrar tipos de relação"
            value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <span className="adm-toolbar__count">
          {filtrados.length} de {tipos.length} {tipos.length === 1 ? 'registro' : 'registros'}
        </span>
      </div>

      {filtrados.length === 0 ? (
        <div className="adm-placeholder">
          <EmptyState icon="fas fa-link"
            title={tipos.length === 0 ? 'Nenhum tipo de relação ainda' : 'Nenhum tipo com esse filtro'}
            description={tipos.length === 0
              ? 'Crie o primeiro para poder ligar Itens de Trabalho entre si.'
              : `Nada casa com “${busca}”.`}
            action={tipos.length === 0
              ? (<Button variant="primary" icon="fas fa-plus" onClick={() => abrir(null)}>Novo tipo</Button>)
              : (<Button variant="secondary" icon="fas fa-times" onClick={() => setBusca('')}>Limpar o filtro</Button>)} />
        </div>
      ) : (
        <div className="adm-list">
          {/* HIERARQUIA: não é um tipo cadastrado, é o pai e filho do item —
              aparece aqui para quem procura "Filho de" na lista de relações
              (SPEC-20261008-1954-itens-relacionados) */}
          {!busca || /filho|pai|hierarq/i.test(busca) ? (
            <div className="adm-catrow">
              <span className="adm-catrow__icon"><i className="fas fa-sitemap" aria-hidden /></span>
              <span className="adm-catrow__main">
                <span className="adm-catrow__name">
                  Filho de <span style={{ color: 'var(--ob-text-subtle)', fontWeight: 400 }}>⇄ Pai de</span>
                </span>
                <span className="adm-catrow__sub">
                  Hierarquia do item · fixo do sistema: ao relacionar com “Filho de”, o item passa a ser filho do outro
                </span>
              </span>
              <Badge tone="neutral" icon="fas fa-lock">
                Fixo
              </Badge>
            </div>
          ) : null}
          {filtrados.map((row) => {
            const confirmando = confirmandoExclusao === row.id
            const nome = row.name['pt-BR'] ?? row.code
            const reverso = row.isSymmetric ? nome : (row.reverse['pt-BR'] ?? '—')
            return (
              <div key={row.id} className={`adm-catrow${row.isActive ? '' : ' adm-catrow--off'}`}>
                <span className="adm-catrow__icon"><i className="fas fa-link" aria-hidden /></span>
                <span className="adm-catrow__main">
                  <span className="adm-catrow__name">{nome} <span style={{ color: 'var(--ob-text-subtle)', fontWeight: 400 }}>⇄ {reverso}</span></span>
                  <span className="adm-catrow__sub">
                    Código {row.code}
                    {row.isSymmetric ? ' · simétrica' : ''}
                    {row.usageCount > 0 ? ` · ${row.usageCount} ${row.usageCount === 1 ? 'ligação' : 'ligações'}` : ' · sem uso'}
                  </span>
                </span>

                {confirmando ? (
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, color: 'var(--ob-danger)' }}>Excluir “{nome}”?</span>
                    <Button variant="danger" loading={excluir.isPending} onClick={() => excluir.mutate(row.id)}>
                      Confirmar
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmandoExclusao(null)}>
                      Cancelar
                    </Button>
                  </span>
                ) : (
                  <>
                    <span className="adm-switchwrap">
                      <span className="adm-switchwrap__label">{row.isActive ? 'Ativo' : 'Inativo'}</span>
                      <button type="button" role="switch" aria-checked={row.isActive}
                        aria-label={`${nome} está ativo`}
                        title={row.isActive ? 'Ativo — clique para desativar' : 'Inativo — clique para ativar'}
                        className={`adm-switch${row.isActive ? ' adm-switch--on' : ''}`}
                        onClick={() => alternarAtivo.mutate({ id: row.id, isActive: !row.isActive })}>
                        <span className="adm-switch__knob" aria-hidden />
                      </button>
                    </span>
                    <button type="button" className="adm-iconbtn" aria-label={`Editar ${nome}`}
                      title="Editar" onClick={() => abrir(row)}>
                      <i className="fas fa-pen" aria-hidden />
                    </button>
                    {row.isSystem ? (
                      <Badge tone="neutral" icon="fas fa-anchor">
                        Âncora
                      </Badge>
                    ) : null}
                    <button type="button" className="adm-iconbtn adm-iconbtn--danger"
                      disabled={row.usageCount > 0 || row.isSystem}
                      aria-label={row.isSystem ? `${row.code} é âncora dos critérios de aceite` : row.usageCount > 0 ? `${row.code} está em uso` : `Excluir ${row.code}`}
                      title={row.isSystem ? 'Âncora dos critérios de aceite: o sistema procura por este código' : row.usageCount > 0 ? 'Tipo em uso: desative-o para tirá-lo das opções' : 'Excluir'}
                      onClick={() => setConfirmandoExclusao(row.id)}>
                      <i className="fas fa-trash-alt" aria-hidden />
                    </button>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}
